import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { Lu, Loader, PRIORITY_META } from "@/components/shared";

export default function StaffApp() {
  const { t, lang } = useLang();
  const { selectedId } = useProperty();
  const [data, setData] = useState(null);

  const load = () => client.get("/staff/tasks").then((r) => setData(r.data));
  useEffect(() => { setData(null); load(); }, [selectedId]);

  const done = async (tk) => {
    const pid = tk.property_id;
    await client.post(`/properties/${pid}/tickets/${tk.id}`, { status: "resolved" });
    setData((d) => ({ ...d, tasks: d.tasks.filter((x) => x.id !== tk.id), summary: { ...d.summary, pending: d.summary.pending - 1 } }));
    toast.success(t("markDone"));
  };

  if (!data) return <Loader />;

  const Phone = (
    <div className="max-w-md mx-auto bg-card rounded-3xl border border-primary/20 shadow-2xl overflow-hidden">
      <div className="p-5 bg-card border-b border-border">
        <p className="text-xs text-primary">{t("staffApp")}</p>
        <p className="text-lg font-bold text-foreground">{t("myTasks")}</p>
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[["pending", data.summary.pending, "#F59E0B"], ["dndRooms", data.summary.dnd, "#8B5CF6"], ["murRooms", data.summary.mur, "#10B981"]].map(([k, v, c]) => (
            <div key={k} className="rounded-xl p-3 text-center" style={{ background: `${c}14` }}>
              <p className="text-2xl font-extrabold font-mono" style={{ color: c }}>{v}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{t(k)}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="p-4 space-y-3 max-h-[520px] overflow-y-auto">
        {data.mur_rooms.length > 0 && (
          <div className="rounded-2xl p-4 bg-emerald-500/8 border border-emerald-500/20">
            <p className="text-sm font-semibold text-emerald-400 flex items-center gap-2 mb-2"><Lu name="bed" className="w-4 h-4" />{t("murRooms")}</p>
            <div className="flex flex-wrap gap-2">
              {data.mur_rooms.map((r) => <span key={r.number} className="px-2.5 py-1 rounded-lg bg-foreground/5 text-xs font-mono text-foreground/80">{r.number}</span>)}
            </div>
          </div>
        )}
        {data.dnd_rooms.length > 0 && (
          <div className="rounded-2xl p-4 bg-violet-500/8 border border-violet-500/20">
            <p className="text-sm font-semibold text-violet-400 flex items-center gap-2 mb-2"><Lu name="moon" className="w-4 h-4" />{t("dndRooms")}</p>
            <div className="flex flex-wrap gap-2">
              {data.dnd_rooms.map((r) => <span key={r.number} className="px-2.5 py-1 rounded-lg bg-foreground/5 text-xs font-mono text-foreground/80">{r.number}</span>)}
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground/70 pt-1">{t("pending")} ({data.tasks.length})</p>
        {data.tasks.map((tk) => {
          const pm = PRIORITY_META[tk.priority];
          return (
            <motion.div key={tk.id} layout data-testid={`staff-task-${tk.id}`} className="rounded-2xl p-4 bg-white/[0.04] border border-border flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${pm.color}1a` }}>
                <Lu name={tk.category === "housekeeping" ? "brush-cleaning" : "wrench"} className="w-4 h-4" style={{ color: pm.color }} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{tk[lang === "ar" ? "title_ar" : "title_en"]}</p>
                <p className="text-xs text-muted-foreground/70">{t("room")} {tk.room_number} · {t(tk.priority)}</p>
              </div>
              <button data-testid={`staff-done-${tk.id}`} onClick={() => done(tk)}
                className="w-9 h-9 rounded-full md-gold-gradient flex items-center justify-center text-primary-foreground active:scale-90 transition-transform shrink-0"><Lu name="check" className="w-4 h-4" /></button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2"><Lu name="smartphone" className="w-6 h-6 text-primary" />{t("staffApp")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{lang === "ar" ? "مهام الفريق والتدبير الفندقي" : "Staff & housekeeping tasks"}</p>
      </div>
      {Phone}
    </div>
  );
}
